import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, Package, Truck, Plane, Home as HomeIcon, MapPin, Clock, X, ChevronLeft } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { TRACKING_STEPS, STEP_LABELS, STEP_DESCRIPTIONS, currentStepIndex } from "../lib/tracking";

const STEP_ICON = {
  ordered: Package,
  preparing: Package,
  handed_to_carrier: Truck,
  shipped: Plane,
  arrived_in_country: MapPin,
  out_for_delivery: Truck,
  delivered: HomeIcon,
  cancelled: X,
};

export default function OrderTracking() {
  const { id } = useParams();
  const { t, lang, isRTL } = useLang();
  const labels = STEP_LABELS[lang] || STEP_LABELS.en;
  const descs = STEP_DESCRIPTIONS[lang] || STEP_DESCRIPTIONS.en;

  const { data: order, isLoading, error } = useQuery({
    queryKey: ["order", id],
    queryFn: async () => (await api.get(`/orders/${id}`)).data,
  });

  if (isLoading) return <div className="py-24 text-center text-[#767676]">{t("loading")}</div>;
  if (error || !order) return <div className="py-24 text-center text-[#767676]">Order not found.</div>;

  const events = order.events || [];
  const idx = currentStepIndex(events);
  const cancelled = idx === -2;
  const lastEvent = events[events.length - 1];
  const currentLabel = cancelled
    ? labels.cancelled
    : lastEvent
    ? labels[lastEvent.step] || labels.ordered
    : labels.ordered;

  return (
    <main data-testid="order-tracking-page" className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
      <Link
        to="/orders"
        className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#767676] hover:text-[#1A1738]"
        data-testid="back-to-orders"
      >
        <ChevronLeft className={`h-3.5 w-3.5 ${isRTL ? "rotate-180" : ""}`} strokeWidth={1.5} />
        {lang === "ar" ? "العودة إلى الطلبات" : "Back to orders"}
      </Link>

      <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#7B5CBF]">
            {lang === "ar" ? "تتبع الطلب" : "Tracking"}
          </div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl leading-tight" data-testid="tracking-current-step">
            {currentLabel}
          </h1>
          <p className="mt-1 text-sm text-[#767676] font-mono" data-testid="tracking-order-number">
            {order.order_number}
          </p>
        </div>
        <div className="text-end">
          <div className="text-xs uppercase tracking-widest text-[#767676]">{t("total")}</div>
          <div className="text-2xl font-medium">${order.total.toFixed(2)}</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-8 rounded-md border border-[#E5E1D8] bg-white p-6 md:p-8">
        {cancelled ? (
          <div className="flex items-center gap-3 text-[#7B5CBF]" data-testid="tracking-cancelled">
            <X className="h-6 w-6" strokeWidth={1.5} />
            <span className="font-medium">{descs.cancelled}</span>
          </div>
        ) : (
          <>
            {/* Desktop: horizontal stepper */}
            <div className="hidden md:block relative" data-testid="tracking-stepper-desktop">
              <div className="flex justify-between relative">
                <div className="absolute top-5 start-0 end-0 h-0.5 bg-[#E5E1D8] rounded-full" />
                <div
                  className="absolute top-5 h-0.5 bg-[#1A1738] rounded-full transition-all duration-500"
                  style={{
                    [isRTL ? "right" : "left"]: 0,
                    width: `${idx <= 0 ? 0 : (idx / (TRACKING_STEPS.length - 1)) * 100}%`,
                  }}
                />
                {TRACKING_STEPS.map((step, i) => {
                  const done = i <= idx;
                  const current = i === idx;
                  const Icon = STEP_ICON[step] || Package;
                  return (
                    <div key={step} className="relative flex flex-col items-center text-center flex-1 z-10">
                      <div
                        className={`h-11 w-11 rounded-full flex items-center justify-center border-2 ${
                          done
                            ? "bg-[#1A1738] border-[#1A1738] text-white"
                            : "bg-white border-[#E5E1D8] text-[#767676]"
                        } ${current ? "ring-4 ring-[#E9D9F5]" : ""}`}
                      >
                        {done && !current ? <Check className="h-5 w-5" strokeWidth={2} /> : <Icon className="h-5 w-5" strokeWidth={1.5} />}
                      </div>
                      <div className={`mt-2 text-[10px] uppercase tracking-wider max-w-[90px] ${done ? "text-[#1A1738] font-medium" : "text-[#767676]"}`}>
                        {labels[step]}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mobile: vertical stepper */}
            <ol className="md:hidden space-y-4" data-testid="tracking-stepper-mobile">
              {TRACKING_STEPS.map((step, i) => {
                const done = i <= idx;
                const current = i === idx;
                const Icon = STEP_ICON[step] || Package;
                return (
                  <li key={step} className="flex items-start gap-3">
                    <div
                      className={`h-9 w-9 rounded-full flex items-center justify-center border-2 shrink-0 ${
                        done ? "bg-[#1A1738] border-[#1A1738] text-white" : "bg-white border-[#E5E1D8] text-[#767676]"
                      } ${current ? "ring-4 ring-[#E9D9F5]" : ""}`}
                    >
                      {done && !current ? <Check className="h-4 w-4" strokeWidth={2} /> : <Icon className="h-4 w-4" strokeWidth={1.5} />}
                    </div>
                    <div>
                      <div className={done ? "font-medium" : "text-[#767676]"}>{labels[step]}</div>
                      <div className="text-xs text-[#767676]">{descs[step]}</div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </div>

      {/* Timeline + shipping details */}
      <div className="mt-8 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 rounded-md border border-[#E5E1D8] bg-white p-6">
          <h2 className="font-display text-lg mb-4">
            {lang === "ar" ? "تحديثات الشحن" : "Shipment updates"}
          </h2>
          {events.length === 0 ? (
            <div className="text-sm text-[#767676]" data-testid="tracking-no-updates">
              {lang === "ar" ? "لا توجد تحديثات بعد." : "No updates yet."}
            </div>
          ) : (
            <ol className="relative border-s-2 border-[#E5E1D8] ms-3" data-testid="tracking-timeline">
              {[...events].reverse().map((e) => {
                const Icon = STEP_ICON[e.step] || Package;
                return (
                  <li key={e.id} className="mb-6 ms-6">
                    <span className="absolute -start-[13px] flex h-6 w-6 items-center justify-center rounded-full bg-[#1A1738] text-white">
                      <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </span>
                    <div className="flex items-baseline justify-between gap-4 flex-wrap">
                      <div className="font-medium">{labels[e.step] || e.step}</div>
                      <time className="text-xs text-[#767676] inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" strokeWidth={1.5} />
                        {new Date(e.occurred_at).toLocaleString()}
                      </time>
                    </div>
                    {e.location && <div className="text-sm text-[#767676] mt-0.5">{e.location}</div>}
                    {e.note && <div className="text-sm mt-1">{e.note}</div>}
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <aside className="rounded-md border border-[#E5E1D8] bg-white p-6 h-fit">
          <h2 className="font-display text-lg mb-4">
            {lang === "ar" ? "الشحن إلى" : "Shipping to"}
          </h2>
          <div className="text-sm space-y-1">
            {order.shipping_name && <div className="font-medium">{order.shipping_name}</div>}
            {order.shipping_address && <div className="text-[#767676]">{order.shipping_address}</div>}
            {(order.shipping_city || order.shipping_country) && (
              <div className="text-[#767676]">
                {[order.shipping_city, order.shipping_country].filter(Boolean).join(", ")}
              </div>
            )}
            {order.shipping_phone && <div className="text-[#767676]">{order.shipping_phone}</div>}
            {!order.shipping_address && !order.shipping_name && (
              <div className="text-[#767676]">—</div>
            )}
          </div>

          <h2 className="font-display text-lg mt-6 mb-3">
            {lang === "ar" ? "المنتجات" : "Items"}
          </h2>
          <ul className="space-y-3 text-sm">
            {(order.items || []).map((it) => {
              const p = it.product || {};
              const name = lang === "ar" ? p.name_ar : p.name_en;
              return (
                <li key={it.product_id} className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded bg-[#F3F0EB] overflow-hidden shrink-0">
                    {p.image && <img src={p.image} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{name}</div>
                    <div className="text-xs text-[#767676]">× {it.quantity}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </aside>
      </div>
    </main>
  );
}
